import { useState } from 'react';

import type { NoteKind } from '../../../../api/venue-marketing';

import { FormAlert } from './FormAlert';
import { ACTION_ITEMS_ANCHOR, composerKinds, NOTE_LIST_COPY } from './marketing-note-lists';
import { MarketingNoteListBody } from './MarketingNoteListBody';
import { MarketingNoteListHeader } from './MarketingNoteListHeader';
import { NoteComposer } from './NoteComposer';
import { useComposerState } from './use-composer-state';
import { useCreateMarketingNote, type NoteList } from './use-marketing-notes';
import { useNoteList } from './use-note-list';
import { useScrollToAnchor } from './use-scroll-to-anchor';
import { marketingErrorMessage } from './venue-marketing-errors';

/** Room above the list when it is scrolled to, for the sticky page header. */
const ANCHOR_SCROLL_MARGIN = 96;

interface MarketingNoteListProps {
  venueId: string;
  list: NoteList;
  /** Whether the server lets the caller add notes and tick items. */
  canWrite: boolean;
  /** A marketing-only user is offered "Marketing note" first. */
  canEditMarketingOnly: boolean;
  /** The venue's region, named in a refusal. */
  regionName: string | null;
  /** Caps the height of the list, which then scrolls on its own. */
  maxHeight?: number;
}

/** One of the two lists on the tab: open action items, or the notes history. */
export function MarketingNoteList(props: MarketingNoteListProps) {
  const { venueId, list, canWrite, canEditMarketingOnly, regionName, maxHeight } = props;
  const copy = NOTE_LIST_COPY[list];
  const anchor = list === 'open' ? ACTION_ITEMS_ANCHOR : null;
  const state = useNoteList(venueId, list);
  const anchorRef = useScrollToAnchor(anchor, state.status === 'success');
  const create = useCreateMarketingNote(venueId);
  const composer = useComposerState();
  const [actionError, setActionError] = useState<string | null>(null);

  const add = async (kind: NoteKind, body: string) => {
    try {
      await create.mutateAsync({ kind, body });
      return null;
    } catch (err) {
      return marketingErrorMessage(err, 'add-note', regionName);
    }
  };

  return (
    // The margin keeps the heading clear of the sticky header when scrolled to.
    <div id={anchor ?? undefined} ref={anchorRef} style={{ scrollMarginTop: ANCHOR_SCROLL_MARGIN }}>
      <MarketingNoteListHeader
        title={copy.title}
        count={state.count}
        countLabel={copy.countLabel}
        addLabel={canWrite && !composer.composing ? copy.addLabel : null}
        addButtonRef={composer.addButtonRef}
        onAdd={() => {
          setActionError(null);
          composer.open();
        }}
      />
      {composer.composing && (
        <div style={{ marginTop: 10 }}>
          <NoteComposer
            kinds={composerKinds(list, canEditMarketingOnly)}
            label={copy.composerLabel}
            placeholder={copy.placeholder}
            busy={create.isPending}
            onAdd={add}
            onClose={composer.close}
          />
        </div>
      )}
      {actionError && (
        <div style={{ marginTop: 10 }}>
          <FormAlert message={actionError} onDismiss={() => setActionError(null)} />
        </div>
      )}
      <div style={maxHeight ? { maxHeight, overflowY: 'auto', paddingRight: 4 } : undefined}>
        <MarketingNoteListBody
          venueId={venueId}
          regionName={regionName}
          copy={copy}
          notes={state.notes}
          total={state.total}
          pageSize={state.pageSize}
          status={state.status}
          loadError={state.loadError}
          loadingMore={state.loadingMore}
          canToggle={canWrite}
          canAdd={canWrite && !composer.composing}
          onRetry={state.retry}
          onMore={state.more}
          onError={setActionError}
        />
      </div>
    </div>
  );
}
